import React from 'react'
import { isNil } from 'ramda'
import {
  Root,
  Trigger,
  Portal,
  Overlay,
  Content,
  Title,
  Description,
  Cancel,
  Action,
} from '@radix-ui/react-alert-dialog'

import {
  overlayStyles,
  alertStyles,
  alertHeaderFooterStyles,
  alertBodyStyles,
} from './styles'
import { Button, ButtonSentiment } from '@sidecar-components/atoms/Button'
import {
  ButtonContainer,
  ContainerAlignment,
} from '@sidecar-components/atoms/ButtonContainer'
import { AlertProps } from './Alert.types'

export const Alert = ({
  action,
  canCancel,
  cancelLabel = 'Cancel',
  children,
  className,
  isOpen,
  defaultOpen,
  onOpenChange,
  onCancel,
  overlay,
  title,
  trigger,
}: AlertProps) => {
  // the trigger that opens the alert
  const triggerContent = trigger && <Trigger asChild>{trigger}</Trigger>

  // the alert content that goes within a variable Root element
  const portalContent = (
    <Portal>
      {/* Non-clickable transparency */}
      {!isNil(overlay) ? overlay : <Overlay className={overlayStyles} />}

      {/* Dialog */}
      <Content className={`${alertStyles} ${className}`} aria-modal="true">
        {/* alert header */}
        <Title className={alertHeaderFooterStyles} asChild>
          <div>
            <h3 className="text-center">{title}</h3>
          </div>
        </Title>

        {/* alert body */}
        <Description className={alertBodyStyles} asChild>
          <div className="[&_p]:sh-body">{children}</div>
        </Description>

        {/* alert footer */}
        <div className={alertHeaderFooterStyles}>
          <ButtonContainer align={ContainerAlignment.CENTER}>
            {/* canCancel is when we only need Radix's default Cancel onClick.
            onCancel is when we want a custom callback.
            Leave both out (default) if we don't want users to cancel the alert. */}
            {(canCancel || onCancel) && (
              <Cancel asChild>
                <Button
                  sentiment={ButtonSentiment.SECONDARY}
                  onClick={onCancel}
                  testId="btn_cancelAlert"
                >
                  {cancelLabel}
                </Button>
              </Cancel>
            )}
            <Action asChild>{action}</Action>
          </ButtonContainer>
        </div>
      </Content>
    </Portal>
  )

  // If any of these values are set ("", undefined, etc), Radix turns the alert into a controlled component
  // where we have to manual control the state handling. That is needed sometimes. But for simple alerts,
  // we want the alert to be uncontrolled and handle that complexity by itself. So the default is to not
  // pass any of the state control variables.
  return defaultOpen || isOpen || onOpenChange ? (
    <Root defaultOpen={defaultOpen} onOpenChange={onOpenChange} open={isOpen}>
      {triggerContent}
      {portalContent}
    </Root>
  ) : (
    <Root>
      {triggerContent}
      {portalContent}
    </Root>
  )
}
